import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';

export interface TabOption {
  value: string;
  label: ReactNode;
}

/**
 * Smooth segmented tabs — an accent pill slides between options instead
 * of hard-swapping a background. Measures the active tab so it works
 * with both equal-width (filters) and variable-width (market names)
 * options, and re-flows on resize / label changes (e.g. live %).
 */
export function CabTabs({
  options,
  value,
  onChange,
  scroll,
  fullWidth,
  className,
}: {
  options: TabOption[];
  value: string;
  onChange: (v: string) => void;
  scroll?: boolean;
  fullWidth?: boolean;
  className?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [ind, setInd] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = refs.current[value];
      if (!el) return;
      const left = el.offsetLeft;
      const width = el.offsetWidth;
      setInd((prev) =>
        prev && prev.left === left && prev.width === width ? prev : { left, width },
      );
    };
    measure();
  });

  useLayoutEffect(() => {
    const onResize = () => {
      const el = refs.current[value];
      if (el) setInd({ left: el.offsetLeft, width: el.offsetWidth });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [value]);

  return (
    <div
      className={clsx(
        'relative flex gap-1 rounded-pb-full border border-pb-border-soft bg-pb-paper2/60 p-1',
        scroll && 'overflow-x-auto',
        fullWidth && 'w-full',
        className,
      )}
    >
      {ind ? (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-1 top-1 rounded-pb-full bg-pb-accent-soft ring-1 ring-pb-accent-line shadow-[0_0_16px_-3px_var(--pb-accent)] transition-all duration-300 ease-out"
          style={{ left: ind.left, width: ind.width }}
        />
      ) : null}
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            ref={(el) => {
              refs.current[o.value] = el;
            }}
            onClick={() => onChange(o.value)}
            className={clsx(
              'pb-focus-ring relative z-10 flex shrink-0 items-center justify-center gap-1.5 rounded-pb-full px-3.5 py-1.5 text-[13px] font-medium transition-colors',
              fullWidth && 'flex-1',
              active ? 'text-pb-accent' : 'text-tg-hint hover:text-tg-text',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
